import React, {forwardRef, Fragment, useEffect, useImperativeHandle, useState} from 'react';
import {Card, Checkbox, Col, Form, Input, InputNumber, Row, Select} from "antd";
import {GUTTER_MD, GUTTER_SM, MARGIN_MD, SPAN_HALF, SPAN_ONE_THIRD, SPAN_TWO_THIRDS} from "../../styles/constants";
import {useForm} from 'react-hook-form/dist/react-hook-form.ie11';
import MultilanguageTagsSelect from "../multilanguage-tags-select";
import {DataLanguageProvider, withDataLanguage} from "../../contexts/DataLanguage";
import {
  getLayoutAnnotationAnticipationExceedingMaxNumRecords,
  getLayoutAnnotationAttachedDataFiles,
  getLayoutAnnotationCriteriaSelection,
  getLayoutAnnotationDataflowHidden,
  getLayoutAnnotationDataflowKeywords,
  getLayoutAnnotationDataflowNotes,
  getLayoutAnnotationDataflowSource,
  getLayoutAnnotationDecimalSeparator,
  getLayoutAnnotationDefaultPresentation,
  getLayoutAnnotationDisabledViewers,
  getLayoutAnnotationAggregateCells,
  getLayoutAnnotationEmptyCellPlaceholder,
  getLayoutAnnotationNumberOfDecimals,
  getLayoutAnnotationReferenceMetadata,
  getLayoutAnnotationTemporalDimOrder,
  setLayoutAnnotationAnticipationExceedingMaxNumRecords,
  setLayoutAnnotationAttachedDataFiles,
  setLayoutAnnotationCriteriaSelection,
  setLayoutAnnotationDataflowHidden,
  setLayoutAnnotationDataflowKeywords,
  setLayoutAnnotationDataflowNotes,
  setLayoutAnnotationDataflowSource,
  setLayoutAnnotationDecimalSeparator,
  setLayoutAnnotationDefaultPresentation,
  setLayoutAnnotationDisabledViewers,
  setLayoutAnnotationAggregateCells,
  setLayoutAnnotationEmptyCellPlaceholder,
  setLayoutAnnotationNumberOfDecimals,
  setLayoutAnnotationReferenceMetadata,
  setLayoutAnnotationTemporalDimOrder
} from "../../utils/annotations";
import MultilanguageZoomableTextArea from "../multilanguage-zoomable-textarea";
import FormList from "../form-list";
import AttachedDataFileForm from "./AttachedDataFileForm";
import DataLanguageSelector from "../data-language-selector";
import {connect} from "react-redux";
import {compose} from "redux";
import useCustomCompareEffect from "use-custom-compare-effect/lib";
import _ from "lodash";

const formItemLayout = {
  labelCol: {span: SPAN_ONE_THIRD},
  wrapperCol: {span: SPAN_TWO_THIRDS}
};

const DEFAULT_PRESENTATION_OPTION_TABLE = 'Table';
const DEFAULT_PRESENTATION_OPTION_GRAPH = 'Graph';
const DEFAULT_PRESENTATION_OPTION_MAP = 'Map';

const getDefaultPresentationTranslations = t => {

  const defaultPresentationTranslations = t => ({
    [DEFAULT_PRESENTATION_OPTION_TABLE]: t('layoutAnnotations.defaultPresentation.table'),
    [DEFAULT_PRESENTATION_OPTION_GRAPH]: t('layoutAnnotations.defaultPresentation.graph'),
    [DEFAULT_PRESENTATION_OPTION_MAP]: t('layoutAnnotations.defaultPresentation.map')
  });

  return defaultPresentationTranslations(t !== undefined ? t : str => str);
};

const CRITERIA_SELECTION_OPTION_ALL_FULL = 'FullAll';
const CRITERIA_SELECTION_OPTION_ALL_PARTIAL = 'PartialAll';
const CRITERIA_SELECTION_OPTION_ALL_PARTIAL_OPTIMIZED = 'PartialAllOptimized';
const CRITERIA_SELECTION_OPTION_STEP_BY_STEP_DYNAMIC = 'Dynamic';
const CRITERIA_SELECTION_OPTION_STEP_BY_STEP_FULL = 'FullStep';
const CRITERIA_SELECTION_OPTION_STEP_BY_STEP_PARTIAL = 'PartialStep';

const getCriteriaSelectionTranslations = t => {

  const criteriaSelectionTranslations = t => ({
    [CRITERIA_SELECTION_OPTION_ALL_FULL]: t('layoutAnnotations.criteriaSelection.allFull'),
    [CRITERIA_SELECTION_OPTION_ALL_PARTIAL]: t('layoutAnnotations.criteriaSelection.allPartial'),
    [CRITERIA_SELECTION_OPTION_ALL_PARTIAL_OPTIMIZED]: t('layoutAnnotations.criteriaSelection.allPartialOptimized'),
    [CRITERIA_SELECTION_OPTION_STEP_BY_STEP_DYNAMIC]: t('layoutAnnotations.criteriaSelection.stepByStepDynamic'),
    [CRITERIA_SELECTION_OPTION_STEP_BY_STEP_FULL]: t('layoutAnnotations.criteriaSelection.stepByStepFull'),
    [CRITERIA_SELECTION_OPTION_STEP_BY_STEP_PARTIAL]: t('layoutAnnotations.criteriaSelection.stepByStepPartial')
  });

  return criteriaSelectionTranslations(t !== undefined ? t : str => str);
};


const GeneralParameters = ({
                             t,
                             forwardedRef,
                             disabled,
                             annotations,
                             annotationsConfig,
                             dataLanguage,
                             appLanguage,
                             isOnlyFileDataflow
                           }) => {

  const [attachedDataFilesLanguage, setAttachedDataFilesLanguage] = useState(dataLanguage || appLanguage);

  const [flagSaveAnnotation, setFlagSaveAnnotation] = useState(
    getLayoutAnnotationDataflowKeywords(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationCriteriaSelection(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDefaultPresentation(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationAttachedDataFiles(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDecimalSeparator(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationNumberOfDecimals(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationReferenceMetadata(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationEmptyCellPlaceholder(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDataflowNotes(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDataflowSource(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDataflowHidden(annotations, annotationsConfig) === true ||
    getLayoutAnnotationTemporalDimOrder(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationDisabledViewers(annotations, annotationsConfig) !== null ||
    getLayoutAnnotationAggregateCells(annotations, annotationsConfig) !== null
  );

  const {register, watch, setValue, handleSubmit} = useForm({
    defaultValues: {
      dataflowKeywords: getLayoutAnnotationDataflowKeywords(annotations, annotationsConfig),
      criteriaSelection: getLayoutAnnotationCriteriaSelection(annotations, annotationsConfig),
      defaultPresentation: getLayoutAnnotationDefaultPresentation(annotations, annotationsConfig),
      attachedDataFiles: getLayoutAnnotationAttachedDataFiles(annotations, annotationsConfig) || {},
      decimalSeparator: getLayoutAnnotationDecimalSeparator(annotations, annotationsConfig),
      numberOfDecimals: getLayoutAnnotationNumberOfDecimals(annotations, annotationsConfig),
      referenceMetadata: getLayoutAnnotationReferenceMetadata(annotations, annotationsConfig),
      emptyCellPlaceholder: getLayoutAnnotationEmptyCellPlaceholder(annotations, annotationsConfig),
      dataflowNotes: getLayoutAnnotationDataflowNotes(annotations, annotationsConfig),
      dataflowSource: getLayoutAnnotationDataflowSource(annotations, annotationsConfig),
      dataflowHidden: getLayoutAnnotationDataflowHidden(annotations, annotationsConfig),
      temporalDimOrder: getLayoutAnnotationTemporalDimOrder(annotations, annotationsConfig),
      disabledViewers: getLayoutAnnotationDisabledViewers(annotations, annotationsConfig),
      aggregateCells: getLayoutAnnotationAggregateCells(annotations, annotationsConfig),
      anticipationExceedingMaxNumRecords: getLayoutAnnotationAnticipationExceedingMaxNumRecords(annotations, annotationsConfig),
    }
  });

  useCustomCompareEffect(() => {

    const form = watch();

    if (
      (form.dataflowKeywords !== null && Object.keys(form.dataflowKeywords).length > 0) ||
      form.criteriaSelection !== null ||
      form.defaultPresentation !== null ||
      (form.attachedDataFiles !== null && Object.keys(form.attachedDataFiles).length > 0) ||
      form.decimalSeparator !== null ||
      form.numberOfDecimals !== null ||
      form.referenceMetadata !== null ||
      form.emptyCellPlaceholder !== null ||
      form.dataflowNotes !== null ||
      form.dataflowSource !== null ||
      form.dataflowHidden === true ||
      form.temporalDimOrder !== null ||
      form.disabledViewers !== null ||
      form.aggregateCells !== null ||
      form.anticipationExceedingMaxNumRecords !== null
    ) {
      setFlagSaveAnnotation(true);
    }

  }, [watch()], _.isEqual);


  useEffect(() => {
    register({name: 'dataflowKeywords'});
    register({name: 'criteriaSelection'});
    register({name: 'attachedDataFiles'});
    register({name: 'defaultPresentation'});
    register({name: 'decimalSeparator'});
    register({name: 'numberOfDecimals'});
    register({name: 'referenceMetadata'});
    register({name: 'emptyCellPlaceholder'});
    register({name: 'dataflowNotes'});
    register({name: 'dataflowSource'});
    register({name: 'dataflowHidden'});
    register({name: 'temporalDimOrder'});
    register({name: 'disabledViewers'});
    register({name: 'aggregateCells'});
    register({name: 'anticipationExceedingMaxNumRecords'});
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    submit(annotations, cb) {
      handleSubmit(val => {

        let newLayoutAnnotations = setLayoutAnnotationDataflowKeywords(annotations, annotationsConfig, flagSaveAnnotation ? val.dataflowKeywords : []);
        newLayoutAnnotations = setLayoutAnnotationCriteriaSelection(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.criteriaSelection : null);
        newLayoutAnnotations = setLayoutAnnotationAttachedDataFiles(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.attachedDataFiles : {});
        newLayoutAnnotations = setLayoutAnnotationDefaultPresentation(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.defaultPresentation : null);
        newLayoutAnnotations = setLayoutAnnotationDecimalSeparator(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.decimalSeparator : null);
        newLayoutAnnotations = setLayoutAnnotationNumberOfDecimals(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.numberOfDecimals : null);
        newLayoutAnnotations = setLayoutAnnotationReferenceMetadata(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.referenceMetadata : null);
        newLayoutAnnotations = setLayoutAnnotationEmptyCellPlaceholder(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.emptyCellPlaceholder : null);
        newLayoutAnnotations = setLayoutAnnotationDataflowNotes(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.dataflowNotes : null);
        newLayoutAnnotations = setLayoutAnnotationDataflowSource(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.dataflowSource : null);
        newLayoutAnnotations = setLayoutAnnotationDataflowHidden(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.dataflowHidden : false);
        newLayoutAnnotations = setLayoutAnnotationTemporalDimOrder(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.temporalDimOrder : null);
        newLayoutAnnotations = setLayoutAnnotationDisabledViewers(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.disabledViewers : null);
        newLayoutAnnotations = setLayoutAnnotationAggregateCells(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.aggregateCells : null);
        newLayoutAnnotations = setLayoutAnnotationAnticipationExceedingMaxNumRecords(newLayoutAnnotations, annotationsConfig, flagSaveAnnotation ? val.anticipationExceedingMaxNumRecords : false);
        
        cb(newLayoutAnnotations);
      })();
    }
  }));


  return (
    <Fragment>
      {!disabled && (
        <Row type="flex" gutter={GUTTER_SM} style={{marginBottom: MARGIN_MD}}>
          <Col>
            {t("components.layoutAnnotationsForm.generalParameters.flagSaveAnnotation.title")}:
          </Col>
          <Col>
            <Checkbox
              checked={flagSaveAnnotation}
              onChange={e => setFlagSaveAnnotation(e.target.checked)}
            />
          </Col>
        </Row>
      )}
      <Form>
        <DataLanguageProvider value={null}>
          {isOnlyFileDataflow
            ? (
              <Fragment>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowKeywords.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageTagsSelect
                        disabled={disabled}
                        value={watch('dataflowKeywords')}
                        onChange={val => setValue('dataflowKeywords', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutReferenceMetadata.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('referenceMetadata')}
                        value={watch('referenceMetadata')}
                        onChange={val => setValue('referenceMetadata', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowNotes.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('dataflowNotes')}
                        value={watch('dataflowNotes')}
                        onChange={val => setValue('dataflowNotes', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowSource.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('dataflowSource')}
                        value={watch('dataflowSource')}
                        onChange={val => setValue('dataflowSource', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Fragment>
            ) : (
              <Fragment>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowKeywords.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageTagsSelect
                        disabled={disabled}
                        value={watch('dataflowKeywords')}
                        onChange={val => setValue('dataflowKeywords', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutCriteriaSelection.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('criteriaSelection')}
                        value={watch('criteriaSelection')}
                        onChange={val => setValue('criteriaSelection', val)}
                      >
                        <Select.Option
                          value={null}
                          title={t('layoutAnnotations.empty')}
                        >
                          {t('layoutAnnotations.empty')}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_ALL_FULL}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_FULL]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_FULL]}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_ALL_PARTIAL}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_PARTIAL]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_PARTIAL]}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_ALL_PARTIAL_OPTIMIZED}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_PARTIAL_OPTIMIZED]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_ALL_PARTIAL_OPTIMIZED]}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_STEP_BY_STEP_DYNAMIC}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_DYNAMIC]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_DYNAMIC]}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_STEP_BY_STEP_FULL}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_FULL]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_FULL]}
                        </Select.Option>
                        <Select.Option
                          value={CRITERIA_SELECTION_OPTION_STEP_BY_STEP_PARTIAL}
                          title={getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_PARTIAL]}
                        >
                          {getCriteriaSelectionTranslations(t)[CRITERIA_SELECTION_OPTION_STEP_BY_STEP_PARTIAL]}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDefaultPresentation.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('defaultPresentation')}
                        value={watch('defaultPresentation')}
                        onChange={val => setValue('defaultPresentation', val)}
                      >
                        <Select.Option
                          value={null}
                          title={t('layoutAnnotations.empty')}
                        >
                          {t('layoutAnnotations.empty')}
                        </Select.Option>
                        <Select.Option
                          value={DEFAULT_PRESENTATION_OPTION_TABLE}
                          title={getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_TABLE]}
                        >
                          {getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_TABLE]}
                        </Select.Option>
                        <Select.Option
                          value={DEFAULT_PRESENTATION_OPTION_GRAPH}
                          title={getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_GRAPH]}
                        >
                          {getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_GRAPH]}
                        </Select.Option>
                        <Select.Option
                          value={DEFAULT_PRESENTATION_OPTION_MAP}
                          title={getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_MAP]}
                        >
                          {getDefaultPresentationTranslations(t)[DEFAULT_PRESENTATION_OPTION_MAP]}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDecimalSeparator.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('decimalSeparator')}
                        value={watch('decimalSeparator')}
                        onChange={val => setValue('decimalSeparator', val)}
                      >
                        <Select.Option
                          value={null}
                          title={t('layoutAnnotations.empty')}
                        >
                          {t('layoutAnnotations.empty')}
                        </Select.Option>
                        <Select.Option value=".">.</Select.Option>
                        <Select.Option value=",">,</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutNumberOfDecimals.label')}
                      {...formItemLayout}
                    >
                      <InputNumber
                        style={{width: "100%"}}
                        min={0}
                        precision={0}
                        disabled={disabled}
                        title={watch('numberOfDecimals')}
                        value={watch('numberOfDecimals')}
                        onChange={val => setValue('numberOfDecimals', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutReferenceMetadata.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('referenceMetadata')}
                        value={watch('referenceMetadata')}
                        onChange={val => setValue('referenceMetadata', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutEmptyCellPlaceholder.label')}
                      {...formItemLayout}
                    >
                      <Input
                        disabled={disabled}
                        title={watch('emptyCellPlaceholder')}
                        value={watch('emptyCellPlaceholder')}
                        onChange={e => setValue('emptyCellPlaceholder', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutHidden.label')}
                      {...formItemLayout}
                    >
                      <Checkbox
                        disabled={disabled}
                        title={watch("dataflowHidden")}
                        checked={watch('dataflowHidden')}
                        onChange={e => setValue('dataflowHidden', e.target.checked)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowNotes.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('dataflowNotes')}
                        value={watch('dataflowNotes')}
                        onChange={val => setValue('dataflowNotes', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutDataflowSource.label')}
                      {...formItemLayout}
                    >
                      <MultilanguageZoomableTextArea
                        placeholder={t('components.urlInput.placeholder.title')}
                        singleLine
                        disabled={disabled}
                        title={watch('dataflowSource')}
                        value={watch('dataflowSource')}
                        onChange={val => setValue('dataflowSource', val)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={GUTTER_MD}>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.temporalDimOrder.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('temporalDimOrder')}
                        value={watch('temporalDimOrder')}
                        onChange={val => setValue('temporalDimOrder', val)}
                      >
                        <Select.Option
                          value={null}
                          title={t('layoutAnnotations.empty')}
                        >
                          {t('layoutAnnotations.empty')}
                        </Select.Option>
                        <Select.Option
                          value="ASC"
                          title={t('layoutAnnotations.temporalDimOrder.asc')}
                        >
                          {t('layoutAnnotations.temporalDimOrder.asc')}
                        </Select.Option>
                        <Select.Option
                          value="DESC"
                          title={t('layoutAnnotations.temporalDimOrder.desc')}
                        >
                          {t('layoutAnnotations.temporalDimOrder.desc')}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.disabledViewers.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('disabledViewers')}
                        value={watch('disabledViewers')}
                        onChange={val => setValue('disabledViewers', val)}
                        mode="multiple"
                      >
                        <Select.Option
                          value={"Table"}
                          title={t('layoutAnnotations.disabledViewers.table')}
                        >
                          {t('layoutAnnotations.disabledViewers.table')}
                        </Select.Option>
                        <Select.Option
                          value="Graph"
                          title={t('layoutAnnotations.disabledViewers.graph')}
                        >
                          {t('layoutAnnotations.disabledViewers.graph')}
                        </Select.Option>
                        <Select.Option
                          value="Map"
                          title={t('layoutAnnotations.disabledViewers.map')}
                        >
                          {t('layoutAnnotations.disabledViewers.map')}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  
                </Row>
                  <Row gutter={GUTTER_MD}>
                    <Col span={SPAN_HALF}>
                    <Form.Item
                        label={t('data.nodesConfig.annotations.anticipationExceedingMaxNumRecords.label')}
                        {...formItemLayout}
                      >
                        <Checkbox
                          disabled={disabled}
                          title={watch("anticipationExceedingMaxNumRecords")}
                          checked={watch('anticipationExceedingMaxNumRecords')}
                          onChange={e => setValue('anticipationExceedingMaxNumRecords', e.target.checked)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={SPAN_HALF}>
                    <Form.Item
                      label={t('data.nodesConfig.annotations.layoutAggregateCells.label')}
                      {...formItemLayout}
                    >
                      <Select
                        disabled={disabled}
                        title={watch('aggregateCells')}
                        value={watch('aggregateCells')}
                        onChange={val => setValue('aggregateCells', val)}
                      >
                        <Select.Option
                          value="JOIN,JOIN"
                          title={t("data.nodesConfig.annotations.aggregateCells.joinJoin")}
                        >
                          {t("data.nodesConfig.annotations.aggregateCells.joinJoin")}
                        </Select.Option>
                        <Select.Option
                          value={"SPLIT,SPLIT"}
                          title={t("data.nodesConfig.annotations.aggregateCells.splitSplit")}
                        >
                          {t("data.nodesConfig.annotations.aggregateCells.splitSplit")}
                        </Select.Option>
                        <Select.Option
                          value={"JOIN,SPLIT"}
                          title={t("data.nodesConfig.annotations.aggregateCells.joinSplit")}
                        >
                          {t("data.nodesConfig.annotations.aggregateCells.joinSplit")}
                        </Select.Option>
                        <Select.Option
                          value={"SPLIT,JOIN"}
                          title={t("data.nodesConfig.annotations.aggregateCells.splitJoin")}
                        >
                          {t("data.nodesConfig.annotations.aggregateCells.splitJoin")}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  </Row>
              </Fragment>
            )}
        </DataLanguageProvider>
      </Form>
      <Card
        type="inner"
        style={{marginTop: MARGIN_MD}}
      >
        <Row type="flex" justify="space-between">
          <Col>
            <Form.Item label={t('data.nodesConfig.annotations.layoutAttachedDataFiles.label')}/>
          </Col>
          <Col>
            <DataLanguageSelector
              value={attachedDataFilesLanguage}
              size="small"
              onSelect={selected => setAttachedDataFilesLanguage(selected)}
            />
          </Col>
        </Row>
        <FormList
          compact
          disabled={disabled}
          values={watch('attachedDataFiles')[attachedDataFilesLanguage] || []}
          Component={AttachedDataFileForm}
          newItem={{
            format: null,
            url: null
          }}
          addItemLabel={t('components.layoutAnnotationsForm.generalParameters.attachedDataFiles.modal.addButton.title')}
          removeItemLabel={t('components.layoutAnnotationsForm.generalParameters.attachedDataFiles.modal.removeButton.title')}
          onChange={val => {
            const newAttachedDataFiles = watch('attachedDataFiles');
            newAttachedDataFiles[attachedDataFilesLanguage] = val;
            setValue('attachedDataFiles', newAttachedDataFiles);
            setFlagSaveAnnotation(true);
          }}
        />
      </Card>
    </Fragment>
  );
};

const ConnectedGeneralParameters = compose(
  withDataLanguage(),
  connect(state => ({
    appLanguage: state.app.language
  })))(GeneralParameters);

export default forwardRef(
  (props, ref) =>
    <ConnectedGeneralParameters {...props} forwardedRef={ref}/>
);